import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPortal from "Components/ReactPortal/ReactPortal";
import PropTypes from "prop-types";
import { LuX } from "react-icons/lu";
import PatientSuperSearch from "NewComponents/UtilityComponents/PatientSuperSearch/PatientSuperSearch";
import PaymentLineItems from "./Components/PaymentLineItems";
import { ErrorHandlingMessage } from "Utils/ErrorHandlingMessage";
import api from "http";
import LoaderWrapper from "Common/LoaderWrapper/LoaderWrapper";
import PaymentSummary from "./Components/PaymentSummary";
import PaymentMethods from "./Components/PaymentMethods/PaymentMethods";
import { toast } from "react-toastify";
import StripeAccountInput from "./Components/StripeAccountInput";
import Show from "Components/Show/Show";
import {
  calculateCreditCardFee,
  calculateDiscount,
  calculateSubtotal,
  calculateTotalDue,
  isDiscountExceedingPayment,
} from "Utils/Utils";
import NumberConversion from "Utils/NumberConversion";
import CustomButtonNew from "Common/CustomButtonNew/CustomButtonNew";
import {
  EXTENSION_NOT_ALLOWED,
  FILE_SIZE_MUST_BE_LESS_THAN_30MB,
  PAYMENT_APPROVED_SUCCESSFULLY,
} from "Common/ToastConstants";
import CardReaderAlertPopup from "./Components/PaymentMethods/CardReaderAlertPopup";
import StripeWrapper from "./StripeWrapper";
import {
  validateLineItems,
  validatePaymentForm,
} from "./NewPatientPayment.validation";
import {
  ALLOWED_FILE_TYPES,
  DEFAULT_LINE_ITEM,
  DEFAULT_PAYMENT_MODEL,
  DEFAULT_VALIDATION_MODEL,
} from "./NewPatientPayment.constants";
import { useCardReader } from "./hooks/useCardReader";
import FullPageLoader from "Components/FullPageLoader/FullPageLoader";
import PaymentNote from "./Components/PaymentNote";
import PaymentAttatchments from "./Components/PaymentAttatchments";
import { dropDownApis, MAX_FILE_SIZE_30MB, TODAY_DATE } from "Common/Constants";
import { patientPaymentTabs } from "MainTabs/PatientPayments/PatientPayments";
import CustomSelect from "Common/CustomSelect";
import useDataFetch from "Common/useDataFetch/useDataFetch";
import isNull from "./../../Utils/NullCheck";

const AddNewPatientPaymentPopup = ({
  currentTab,
  onClose,
  handleSubmit,
  patientId,
  onPaymentConfirmed,
  initialCoPaymentAmount,
  initialServiceDate,
}) => {
  const UNIQUE_ID = "AddNewPatientPaymentPopup";

  const [paymentModel, setPaymentModel] = useState(DEFAULT_PAYMENT_MODEL);
  const [lineItems, setLineItems] = useState([DEFAULT_LINE_ITEM]);
  const [patientDetail, setPatientDetail] = useState({
    name: "",
    balance: 0,
    email: "",
    phone: "",
  });
  const [patientLoader, setPatientLoader] = useState(false);
  const [validationModel, setValidationModel] = useState(
    DEFAULT_VALIDATION_MODEL
  );
  const [saveApiLoader, setSaveApiLoader] = useState(false);
  const [lineItemsValidationModel, setLineItemsValidationModel] = useState([
    { serviceDateError: null, itemDescriptionError: null, itemTypeError: null },
  ]);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);
  const [showCardReaderAlert, setShowCardReaderAlert] = useState({
    status: false,
    readerName: "",
  });
  const { abortControllerRef, paymentStatusPool, handleCancelTerminalPayment } =
    useCardReader();

  const cardFormRef = useRef(null);
  const { data: FindLocations } = useDataFetch(
    dropDownApis.pms.findLocations,
    "findLocations"
  );

  const setValidation = () => {
    const { validationObj, hasError } = validatePaymentForm(
      paymentModel,
      validationModel
    );
    setValidationModel(validationObj);
    return hasError;
  };

  const setLineItemsValidation = () => {
    const { validationArr, hasError } = validateLineItems(
      lineItems,
      lineItemsValidationModel,
      patientDetail.balance
    );
    setLineItemsValidationModel(validationArr);
    return hasError;
  };

  // ******** Line Items functions start ********
  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      {
        ...DEFAULT_LINE_ITEM,
        identifier: lineItems.length,
        itemType: "",
        amount: 0,
      },
    ]);
    setLineItemsValidationModel((prev) => [...prev, { amountError: null }]);
  };
  const removeLineItem = (identifier, index) => {
    setLineItems((prev) =>
      prev.filter((item, i) => item.identifier !== identifier)
    );
    setLineItemsValidationModel((prev) =>
      prev.filter((item, i) => i !== index)
    );
  };
  const updateLineItem = (identifier, field, value) => {
    // if length is more then 100 characters then  splice the value to 100 characters
    let updatedValue = value;
    if (field === "itemDescription") {
      if (value.length > 100) {
        updatedValue = value.slice(0, 100);
      }
    }

    if (field === "itemType") {
      if (value !== "OUTBAL") {
        if (value === "COP") {
          setLineItems((prev) =>
            prev.map((item) =>
              item.identifier === identifier
                ? {
                    ...item,
                    [field]: updatedValue,
                    amount: 0,
                    serviceDate: TODAY_DATE,
                  }
                : item
            )
          );
        } else {
          setLineItems((prev) =>
            prev.map((item) =>
              item.identifier === identifier
                ? { ...item, [field]: updatedValue, amount: 0 }
                : item
            )
          );
        }
      } else {
        setLineItems((prev) =>
          prev.map((item) =>
            item.identifier === identifier
              ? {
                  ...item,
                  [field]: updatedValue,
                  amount: patientDetail.balance,
                }
              : item
          )
        );
      }
    } else {
      setLineItems((prev) =>
        prev.map((item) =>
          item.identifier === identifier
            ? { ...item, [field]: updatedValue }
            : item
        )
      );
    }
  };
  // ******** Line Items functions end ********

  // ******** Payment Summary functions start ********
  const handlePaymentSummary = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentModel((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  // ******** Payment Summary functions end ********

  // ******** Payment Methods functions start ********
  const handlePaymentMethod = (e) => {
    const { name, value } = e.target;
    if (name === "patientPaymentType") {
      switch (value) {
        case "PAY_REC":
          setPaymentModel((prev) => ({
            ...prev,
            [name]: value,
            requestEmail: "",
            requestPhone: "",
            paymentMethodId: "",
            cardType: "",
            last4Digits: "",
            cardPaymentMethod: "SAVED",
            stripeSettingsId: "",
          }));
          break;
        case "PAY_REQ": {
          let email = "";
          let phone = "";
          if (paymentModel.requestedVia.toLowerCase() === "email") {
            email = patientDetail.email;
            phone = "";
          } else if (paymentModel.requestedVia.toLowerCase() === "text") {
            phone = patientDetail.phone;
            email = "";
          }
          setPaymentModel((prev) => ({
            ...prev,
            [name]: value,
            checkNumber: "",
            receivedDate: "",
            paymentMethodId: "",
            cardType: "",
            last4Digits: "",
            cardPaymentMethod: "SAVED",
            requestEmail: email,
            requestPhone: phone,
            requestedTo: email || phone,
            stripeSettingsId: "",
          }));
          break;
        }
        case "CC_PMT":
          setPaymentModel((prev) => ({
            ...prev,
            [name]: value,
            checkNumber: "",
            receivedDate: "",
            requestEmail: "",
            requestPhone: "",
            stripeSettingsId: "",
          }));
          break;
        default:
          setPaymentModel((prev) => ({
            ...prev,
            [name]: value,
            stripeSettingsId: "",
          }));
          setStripePublishableKey("");
          break;
      }
      setPaymentModel((prev) => ({
        ...prev,
        [name]: value,
        stripeSettingsId: "",
      }));
      setStripePublishableKey("");
    } else if (name === "requestedVia") {
      if (value === "Email") {
        setPaymentModel((prev) => ({
          ...prev,
          [name]: value,
          requestEmail: patientDetail.email,
          requestPhone: "",
          requestedTo: patientDetail.email,
        }));
      } else if (value === "Text") {
        setPaymentModel((prev) => ({
          ...prev,
          [name]: value,
          requestPhone: patientDetail.phone,
          requestEmail: "",
          requestedTo: patientDetail.phone,
        }));
      }
    } else if (name === "terminalId") {
      setPaymentModel((prev) => ({
        ...prev,
        [name]: value,
        readerName: value,
      }));
    } else if (name === "requestEmail") {
      setPaymentModel((prev) => ({
        ...prev,
        [name]: value,
        requestedTo: value,
      }));
    } else if (name === "requestPhone") {
      setPaymentModel((prev) => ({
        ...prev,
        [name]: value,
        requestedTo: value,
      }));
    } else {
      setPaymentModel((prev) => ({ ...prev, [name]: value }));
    }
  };
  // ******** Payment Methods functions end ********

  const handleCardReaderAlertClose = () => {
    setShowCardReaderAlert({ status: false, readerName: "" });
    handleCancelTerminalPayment(
      paymentModel.stripeSettingsId,
      paymentModel.terminalId
    );
  };

  // ******** Payment Note functions start ********
  const handlePaymentNote = (e) => {
    const { name, value } = e.target;
    const limitedValue = value.length > 500 ? value.slice(0, 500) : value;
    setPaymentModel((prev) => ({ ...prev, [name]: limitedValue }));
  };
  // ******** Payment Note functions end ********

  // ******** Payment Attachments functions start ********
  const handleAttachmentChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const allowedAttachments = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      let fileType =
        selectedFiles[i].type || selectedFiles[i].name.split(".")[1];
      if (selectedFiles[i] && ALLOWED_FILE_TYPES.includes(fileType)) {
        if (MAX_FILE_SIZE_30MB > selectedFiles[i].size) {
          const reader = new FileReader();
          reader.onload = (event) => {
            allowedAttachments.push({
              addedBy: "",
              updatedBy: "",
              updatedDate: "",
              inActive: false,
              inActiveReason: "",
              inActiveBy: "",
              inActiveDate: "",
              id: 0,
              paymentMainId: 0,
              paymentMain: "",
              fileName: selectedFiles[i].name,
              url: "",
              fileBase64: event.target.result,
            });
            if (allowedAttachments.length === selectedFiles.length) {
              setPaymentModel((prev) => ({
                ...prev,
                paymentAttachments: [
                  ...(Array.isArray(prev.paymentAttachments)
                    ? prev.paymentAttachments
                    : []),
                  ...allowedAttachments,
                ],
              }));
            }
          };
          reader.readAsDataURL(selectedFiles[i]);
        } else {
          toast.warning(FILE_SIZE_MUST_BE_LESS_THAN_30MB);
        }
      } else {
        toast.warning(EXTENSION_NOT_ALLOWED);
      }
    }
  };
  const handleRemoveAttachment = (item, itemIndex) => {
    setPaymentModel((prev) => ({
      ...prev,
      paymentAttachments: paymentModel.paymentAttachments.filter(
        (i, index) => index !== itemIndex
      ),
    }));
  };
  // ******** Payment Attachments functions end ********

  // ******** Credit Card functions start ********
  const handleCreditCardChange = useCallback((e, creditCards) => {
    const { name, value } = e.target;
    if (value === "no-payment-id") {
      // it means new card is added
      setPaymentModel((prev) => ({
        ...prev,
        [name]: value,
        cardPaymentMethod: "NEW",
        cardType: "",
        last4Digits: "",
      }));
    } else {
      const cardInfo = creditCards.find((card) => card.id === value);
      if (cardInfo) {
        setPaymentModel((prev) => ({
          ...prev,
          [name]: value,
          cardType: cardInfo?.cardType,
          last4Digits: cardInfo?.last4Digits,
          cardPaymentMethod: cardInfo.cardPaymentMethod || "SAVED",
        }));
      } else {
        setPaymentModel((prev) => ({
          ...prev,
          [name]: value,
          cardPaymentMethod: "SAVED",
          cardType: "",
          last4Digits: "",
        }));
      }
    }
  }, []);
  // ******** Credit Card functions end ********

  // ******** Stripe Account functions start ********
  const handleStripeAccount = useCallback(
    (stripeSettingsId, publishableKey) => {
      setPaymentModel((prev) => ({
        ...prev,
        stripeSettingsId: stripeSettingsId,
      }));
      setStripePublishableKey(publishableKey);
    },
    []
  );
  // ******** Stripe Account functions end *******

  const handleSelectedData = async (selectedObj) => {
    if (selectedObj) {
      try {
        setPatientLoader(true);
        const getPatientInfo = await api.get(
          `${process.env.REACT_APP_TC_PMS}/api/Patient/FindPatientHeaderInfoById/${selectedObj.id}`
        );
        let {
          patientBalance,
          patientEmail,
          patientPhoneNumber,
          name,
          accountNum,
        } = getPatientInfo.data.dataModel;
        patientBalance = patientBalance || 0;
        setPaymentModel((prev) => ({
          ...prev,
          patientId: selectedObj.id,
          requestEmail: patientEmail,
          requestPhone: patientPhoneNumber,
          accountNumber: accountNum,
          requestedTo: patientEmail || patientPhoneNumber,
        }));
        setPatientDetail((prev) => ({
          ...prev,
          name: name,
          balance: patientBalance,
          email: patientEmail,
          phone: patientPhoneNumber,
        }));
        // add patient balance  in  the first  index of line item
        const updatedLineItems = [...lineItems].map((item, i) => {
          return i === 0 ? { ...item, amount: patientBalance } : item;
        });
        setLineItems(updatedLineItems);
      } catch (error) {
        ErrorHandlingMessage(error);
      } finally {
        setPatientLoader(false);
      }
    } else {
      setPaymentModel((prev) => ({
        ...prev,
        patientId: "",
        accountNumber: "",
        requestEmail: "",
        requestPhone: "",
      }));
      setPatientDetail((prev) => ({
        ...prev,
        name: "",
        balance: 0,
        email: "",
        phone: "",
      }));
      // remove just patient balance  in  the first  index of line item
      const updatedLineItems = [...lineItems].map((item, i) => {
        return i === 0 ? { ...item, amount: "" } : item;
      });
      setLineItems(updatedLineItems);
    }
  };

  const isDiscountExceeding = isDiscountExceedingPayment(
    paymentModel,
    lineItems
  );

  const handleConfirmPayment = async () => {
    let validation = setValidation();
    let lineItemsValidation = setLineItemsValidation();
    if (validation || lineItemsValidation) {
    } else {
      let paymentMethodId = paymentModel.paymentMethodId;
      let paidAmount = calculateTotalDue(paymentModel, lineItems);
      const subtotal = calculateSubtotal(lineItems);
      const discount = calculateDiscount(paymentModel, lineItems);
      // discount amount should not be greater than subtotal
      if (discount > subtotal) {
        toast.error("Discount amount should not be greater than subtotal");
        return;
      }

      // geting the newly added card paymentMethod.id if  paymentModel.cardPaymentMethod === "NEW"
      if (paymentModel.patientPaymentType === "CC_PMT") {
        if (paymentModel.cardPaymentMethod === "NEW") {
          if (!cardFormRef.current) {
            toast.error("Card form is not ready. Please try again.");
            return;
          }

          const newPaymentMethodId = await cardFormRef.current.handleSaveCard();
          if (newPaymentMethodId) {
            setPaymentModel((prev) => ({
              ...prev,
              paymentMethodId: newPaymentMethodId,
            }));
            paymentMethodId = newPaymentMethodId;
          } else {
            toast.error("Failed to create payment method");
            return; // Don't proceed if card creation failed
          }
        }
      }

      try {
        setSaveApiLoader(true);
        const payload = {
          ...paymentModel,
          paidAmount: paidAmount,
          paymentMethodId: paymentMethodId,
          lineItems: lineItems.filter((item) => !isNull(item.amount)), // remove the line items with amount 0
          ccFees: calculateCreditCardFee(
            paymentModel,
            lineItems.filter((item) => isNull(item.amount) !== 0),
            2.7
          ),
        };

        const resp = await api.post(
          `${process.env.REACT_APP_TC_PMS}/api/PatientPayment/V2/AddPatientPayment`,
          payload
        );
        // card reader payment
        if (
          paymentModel.patientPaymentType === "CC_RDR" &&
          resp.data.dataModel.paymentIntentId
        ) {
          let paymentIntentId = resp.data.dataModel.paymentIntentId;
          const { signal } = abortControllerRef.current;
          toast.success(resp.data.responseMessage);
          setShowCardReaderAlert({
            status: true,
            readerName: paymentModel.terminalId,
          });
          let promise = new Promise(async (resolve, reject) => {
            let attempts = 0;
            try {
              let statusChecker = async () => {
                let response = await paymentStatusPool(paymentIntentId);
                let status = response.data.dataModel.paymentStatus;

                if (status === "Approved") {
                  attempts = 0;
                  resolve(response.data.dataModel);
                } else if (status === "Pending" && attempts <= 3) {
                  attempts += 1;
                  toast.warn(response.data.dataModel.message);
                  // here
                  if (attempts === 4) {
                    handleCancelTerminalPayment();
                  }
                  if (attempts === 5) {
                    api
                      .post(
                        `${process.env.REACT_APP_TC_PMS}/api/PatientPayment/SimulatePresentMethod`,
                        {
                          terminalId: paymentModel.terminalId,
                          stripeSettingId: paymentModel.stripeSettingsId,
                        },
                        { signal }
                      )
                      .then(async (simulateResp) => {
                        toast.success(simulateResp.data.responseMessage);
                        await statusChecker();
                      })
                      .catch((err) => {
                        ErrorHandlingMessage(err);
                      });
                  } else {
                    await statusChecker();
                  }
                } else if (attempts === 4) {
                  attempts = 0;
                  reject("Request Timeout, Try Again");
                } else {
                  attempts = 0;
                  reject("Invalid Status Received");
                }
              };
              await statusChecker();
            } catch (error) {
              ErrorHandlingMessage(error);
            }
          });
          promise
            .then(async (response) => {
              let paymentMainID = response.paymentMainId;

              toast.success(PAYMENT_APPROVED_SUCCESSFULLY);

              if (onPaymentConfirmed) {
                await onPaymentConfirmed();
              }
              onClose(paymentMainID);
              handleSubmit();
            })
            .catch((poolError) => {
              ErrorHandlingMessage(poolError);
              toast.error(poolError);
            });
        } else if (
          paymentModel.patientPaymentType === "CC_PMT" &&
          paymentModel.cardPaymentMethod === "NEW" &&
          resp.data.dataModel.clientSecret
        ) {
          const { error: confirmError } = await stripePromise.confirmPayment({
            clientSecret: resp.data.dataModel.clientSecret,
            confirmParams: {
              payment_method: paymentMethodId,
              return_url: `${window.location.origin}/completion`,
            },
            redirect: "if_required",
          });
          if (confirmError) {
            toast.error(confirmError.message);
            return;
          }
          toast.success(PAYMENT_APPROVED_SUCCESSFULLY);
          if (onPaymentConfirmed) {
            await onPaymentConfirmed();
          }
          onClose(true);
          handleSubmit();
        } else {
          toast.success(resp.data.responseMessage);

          if (onPaymentConfirmed) {
            await onPaymentConfirmed();
          }
          onClose(true);
          handleSubmit();
        }
      } catch (error) {
        ErrorHandlingMessage(error);
      } finally {
        setSaveApiLoader(false);
      }
    }
  };
  useEffect(() => {
    if (FindLocations?.length > 0 && FindLocations?.length === 1) {
      setPaymentModel((prev) => ({ ...prev, locationId: FindLocations[1].id }));
    }
  }, [FindLocations]);

  useEffect(() => {
    if (currentTab === patientPaymentTabs["Payment Requests"]) {
      setPaymentModel((prev) => ({ ...prev, patientPaymentType: "PAY_REQ" }));
    }
    const abortController = abortControllerRef.current;
    if (patientId) {
      (async () => {
        try {
          setPatientLoader(true);
          const getPatientInfo = await api.get(
            `${process.env.REACT_APP_TC_PMS}/api/Patient/FindPatientHeaderInfoById/${patientId}`
          );
          let {
            patientBalance,
            patientEmail,
            patientPhoneNumber,
            name,
            accountNum,
          } = getPatientInfo.data.dataModel;
          patientBalance = patientBalance || 0;
          setPaymentModel((prev) => ({
            ...prev,
            patientId: patientId,
            accountNumber: accountNum,
            requestEmail: patientEmail,
            requestPhone: patientPhoneNumber,
            requestedTo: patientEmail || patientPhoneNumber,
          }));
          setPatientDetail((prev) => ({
            ...prev,
            name: name,
            balance: patientBalance,
            email: patientEmail,
            phone: patientPhoneNumber,
          }));
          // add patient balance in the first index of line item, or Co-Payment line when opened from eligibility
          const amount = Number(initialCoPaymentAmount);
          const isCoPaymentOpen = amount > 0;
          const serviceDate = initialServiceDate || TODAY_DATE;
          const lineItem = isCoPaymentOpen
            ? [{ ...DEFAULT_LINE_ITEM, identifier: 0, itemType: "COP", amount, serviceDate }]
            : [{ ...DEFAULT_LINE_ITEM, amount: patientBalance }];
          setLineItems(lineItem);
        } catch (error) {
          ErrorHandlingMessage(error);
        } finally {
          setPatientLoader(false);
        }
      })();
    }
    return () => {
      abortController.abort();
    };
  }, [abortControllerRef, patientId, currentTab, initialCoPaymentAmount, initialServiceDate]);
  const disabledButton =
    saveApiLoader ||
    isDiscountExceeding ||
    calculateTotalDue(paymentModel, lineItems) === 0;
  return (
    // wrapperId == any unique id , for uniqueness give componentname as a wrapperID
    <ReactPortal wrapperId={UNIQUE_ID}>
      {saveApiLoader && <FullPageLoader allowFullScreen />}
      <StripeWrapper
        publishableKey={stripePublishableKey}
        stripePromise={stripePromise}
        setStripePromise={setStripePromise}
      >
        <div className="fixed inset-0 z-10 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-20 flex items-center justify-between px-6 py-4 bg-white border-b border-slate-200">
              <h2 className="text-xl font-semibold text-slate-900">
                Add Payment
              </h2>
              <button
                onClick={() => onClose()}
                className="transition-colors text-slate-400 hover:text-slate-600"
              >
                <LuX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <Show when={showCardReaderAlert.status}>
                  <CardReaderAlertPopup
                    readerName={showCardReaderAlert.readerName}
                    onClose={handleCardReaderAlertClose}
                  />
                </Show>
                <label className="block mb-2 text-sm font-medium required ">
                  Patient Name
                </label>
                <LoaderWrapper
                  loading={patientLoader}
                  className="mb-4 h-[30px]"
                >
                  <PatientSuperSearch
                    disabled={patientId ? true : false}
                    searchValue={patientDetail.name}
                    onChange={(value) => {
                      setPatientDetail((prev) => ({ ...prev, name: value }));
                    }}
                    handleSelectedData={handleSelectedData}
                    width="100%"
                  />
                  {validationModel.patientIdError}
                </LoaderWrapper>
              </div>
              {/* <div>
                                <AsyncSelect
                                    name="locationId"
                                    required
                                    labelClass="block text-sm font-medium text-slate-700"
                                    value={paymentModel.locationId}
                                    onChange={handlePaymentSummary}
                                    label="Service Location"
                                    apiLink={`${process.env.REACT_APP_TC_PMS}/api/DropDown/FindLocations`}
                                />
                               
                            </div> */}
              <div>
                <CustomSelect
                  onChange={handlePaymentSummary}
                  required
                  label="Service Location"
                  name="locationId"
                  value={paymentModel.locationId}
                >
                  {FindLocations?.map((loc) => (
                    <option id={loc.id} value={loc.value}>
                      {loc.label}
                    </option>
                  ))}
                </CustomSelect>
                {validationModel.locationIdError}
              </div>
              <PaymentLineItems
                lineItems={lineItems}
                addLineItem={addLineItem}
                removeLineItem={removeLineItem}
                updateLineItem={updateLineItem}
                lineItemsValidationModel={lineItemsValidationModel}
                patientDetail={patientDetail}
              />
              {/* Divider */}
              <div className="border-t border-slate-200"></div>
              <PaymentMethods
                paymentModel={paymentModel}
                handlePaymentMethod={handlePaymentMethod}
                handleCreditCardChange={handleCreditCardChange}
                validationModel={validationModel}
                cardFormRef={cardFormRef}
                publishableKey={stripePublishableKey}
              />
              <Show
                when={
                  paymentModel.patientPaymentType === "CC_PMT" ||
                  paymentModel.patientPaymentType === "CC_RDR" ||
                  paymentModel.patientPaymentType === "PAY_REQ"
                }
              >
                <StripeAccountInput
                  stripeSettingsId={paymentModel.stripeSettingsId}
                  patientPaymentType={paymentModel.patientPaymentType}
                  key={paymentModel.patientPaymentType}
                  handleStripeAccount={handleStripeAccount}
                  validationModel={validationModel}
                />
              </Show>
              <PaymentNote
                note={paymentModel.notes}
                handlePaymentNote={handlePaymentNote}
              />
              <Show when={paymentModel.patientPaymentType === "PAY_REC"}>
                <PaymentAttatchments
                  paymentAttatchment={paymentModel.paymentAttachments}
                  handleAttachmentChange={handleAttachmentChange}
                  handleRemoveAttachment={handleRemoveAttachment}
                />
              </Show>
              <PaymentSummary
                paymentModel={paymentModel}
                handlePaymentSummary={handlePaymentSummary}
                lineItems={lineItems}
                validationModel={validationModel}
              />
            </div>
            {/* Modal Footer */}
            <div className="sticky bottom-0 flex items-center justify-end gap-3 px-6 py-4 border-t bg-slate-50 border-slate-200">
              <CustomButtonNew
                label="Cancel"
                variant="outline"
                onClick={() => onClose()}
              />
              <CustomButtonNew
                label={`Confirm Payment ${NumberConversion(
                  calculateTotalDue(paymentModel, lineItems)
                )}`}
                onClick={() => handleConfirmPayment()}
                disabled={disabledButton}
              />
            </div>
          </div>
        </div>
      </StripeWrapper>
    </ReactPortal>
  );
};

export default AddNewPatientPaymentPopup;

AddNewPatientPaymentPopup.propTypes = {
  onClose: PropTypes.func.isRequired,
  initialCoPaymentAmount: PropTypes.number,
  initialServiceDate: PropTypes.string,
};
